import { useParams, Link } from 'react-router-dom';
import { locations } from '../../../mocks/locations';
import { services } from '../../../mocks/services';
import RevealOnScroll from '../../../components/base/RevealOnScroll';

const HERO_IMAGE = 'https://public.readdy.ai/ai/img_res/ec278546173f1f692219811fb3836355.jpg';
const PHONE = '(708) 698-4072';
const PHONE_HREF = 'tel:7086984072';

export default function LocationDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = locations.find((l) => l.slug === slug);

  if (!location) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-brand-cream">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold text-brand-dark mb-3">Location Not Found</h1>
          <Link to="/locations" className="text-brand-green font-semibold hover:underline">Back to Locations</Link>
        </div>
      </main>
    );
  }

  return (
    <main>
      {/* Hero */}
      <section
        className="relative flex items-end min-h-[400px] pt-40 pb-16 overflow-hidden"
        style={{ backgroundImage: `url('${HERO_IMAGE}')`, backgroundSize: 'cover', backgroundPosition: 'center 30%' }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/92 via-black/55 to-black/15" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <nav className="flex items-center gap-2 text-xs text-white/45 mb-4">
            <Link to="/" className="hover:text-white cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/locations" className="hover:text-white cursor-pointer">Locations</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white/75">{location.city}, IL</span>
          </nav>
          <div className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-map-pin-2-fill text-brand-gold text-xs" />
            <span className="text-brand-gold text-xs font-bold uppercase tracking-widest">Local Service</span>
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Premium Roofing &amp; Construction<br />
            <span className="text-brand-gold italic">in {location.city}, IL</span>
          </h1>
          <p className="text-white/75 max-w-xl leading-relaxed mb-6">
            Serving homeowners and businesses in {location.city} with expert roofing, tuckpointing, siding,
            and exterior construction — family-owned, licensed &amp; insured since 1989.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="bg-brand-gold hover:bg-brand-gold-dark text-white text-sm font-bold px-8 py-3.5 rounded-md transition-colors cursor-pointer whitespace-nowrap">
              Get Free Inspection in {location.city}
            </Link>
            <a href={PHONE_HREF} className="border-2 border-white/40 hover:border-white text-white text-sm font-bold px-8 py-3.5 rounded-md transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-brand-gold" />
              {PHONE}
            </a>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-brand-dark py-5 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-wrap items-center justify-center gap-6 lg:gap-10">
          {[`Local Service in ${location.city}`, 'Proven Results', 'Licensed & Insured', `Trusted in ${location.county}`].map((badge) => (
            <span key={badge} className="flex items-center gap-1.5 text-xs text-gray-400 whitespace-nowrap">
              <i className="ri-shield-check-fill text-brand-gold text-xs" />
              {badge}
            </span>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="bg-brand-cream py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <RevealOnScroll className="text-center mb-10">
            <span className="text-brand-green text-xs font-bold uppercase tracking-widest">Available in {location.city}</span>
            <h2 className="font-display text-3xl font-bold text-brand-dark mt-3">
              Roofing &amp; Exterior Services in {location.city}
            </h2>
            <p className="text-gray-600 mt-3 text-sm max-w-xl mx-auto">
              Green World Construction provides the following services throughout {location.city} and {location.county}.
            </p>
          </RevealOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {services.map((service, idx) => (
              <RevealOnScroll key={service.id} delay={idx * 60}>
                <Link to={`/services/${service.slug}`} className="group bg-white rounded-xl overflow-hidden border border-brand-cream-3 hover:border-brand-gold hover:-translate-y-1 transition-all cursor-pointer">
                  <div className="w-full h-36 overflow-hidden">
                    <img src={service.image} alt={`${service.name} in ${location.city}`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-brand-dark text-sm mb-1">{service.name}</h3>
                    <p className="text-xs text-gray-500 mb-3 leading-relaxed line-clamp-2">{service.shortDesc}</p>
                    <span className="text-xs font-bold text-brand-green flex items-center gap-1 group-hover:gap-2 transition-all">
                      Learn More <i className="ri-arrow-right-line text-xs" />
                    </span>
                  </div>
                </Link>
              </RevealOnScroll>
            ))}
          </div>
          <RevealOnScroll className="text-center">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-green-dark text-white text-sm font-bold px-8 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap">
              Get Free Inspection in {location.city}
              <i className="ri-arrow-right-line" />
            </Link>
          </RevealOnScroll>
        </div>
      </section>

      {/* AI Answer Block */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <RevealOnScroll>
            <h2 className="font-display text-3xl font-bold text-brand-dark text-center mb-8">
              {location.city} Roofing — Common Questions
            </h2>
          </RevealOnScroll>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              {
                q: `What services are available in ${location.city}?`,
                a: `Green World Construction provides complete roofing and exterior construction services throughout ${location.city}, ${location.county}. Services include flat roofing, shingle roofing, commercial roofing, tuckpointing, masonry, chimney repair, sheet metal, vinyl siding, and asphalt sealcoating.`,
              },
              {
                q: `Why choose a local roofing contractor in ${location.city}?`,
                a: `Local expertise ensures proper material selection, drainage planning, and installation methods that account for ${location.city}'s specific climate and building types. Green World has served ${location.county} since 1989 and knows local code requirements.`,
              },
              {
                q: `What are the benefits of professional roofing in ${location.city}?`,
                a: `Professional installation protects your property value, prevents costly water damage, and ensures long-term durability through Chicago winters. Every Green World project includes a free inspection and written estimate.`,
              },
              {
                q: `How do I schedule roofing services in ${location.city}?`,
                a: `Call us at (708) 698-4072 or fill out our online form. We'll schedule a free on-site inspection in ${location.city}, provide a written report, and give you a detailed estimate — no obligation.`,
              },
            ].map((item, idx) => (
              <RevealOnScroll key={idx} delay={idx * 70}>
                <div className="bg-brand-cream rounded-xl p-6 border border-brand-cream-3 hover:border-brand-green/30 transition-all h-full">
                  <h3 className="font-bold text-brand-dark mb-3 text-sm flex items-start gap-2">
                    <i className="ri-question-line text-brand-green text-sm mt-0.5 flex-shrink-0" />
                    {item.q}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed">{item.a}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-brand-dark py-14 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #c9a227 0, #c9a227 1px, transparent 0, transparent 20px)', backgroundSize: '28px 28px' }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <RevealOnScroll className="text-center mb-8">
            <h2 className="font-display text-2xl font-bold text-white">Our Process in {location.city}</h2>
          </RevealOnScroll>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {['Consultation', 'Inspection & Estimate', 'Installation', 'Final Review'].map((step, idx) => (
              <RevealOnScroll key={step} delay={idx * 80}>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-brand-gold/15 border border-brand-gold/25 rounded-full mx-auto mb-3">
                    <span className="font-display text-brand-gold font-bold text-sm">{String(idx + 1).padStart(2, '0')}</span>
                  </div>
                  <span className="text-white text-xs font-semibold">{step}</span>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-brand-cream py-14">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <RevealOnScroll className="text-center mb-7">
            <h2 className="font-display text-2xl font-bold text-brand-dark">Why Green World in {location.city}?</h2>
          </RevealOnScroll>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: 'ri-history-line', t: 'Local Expertise', d: 'Serving your area since 1989 with deep knowledge of local building types.' },
              { icon: 'ri-shield-check-line', t: 'Reliable Execution', d: 'Licensed, insured, and committed to delivering quality on time.' },
              { icon: 'ri-award-line', t: 'High-End Results', d: 'Premium materials, expert installation, and a written quality guarantee.' },
            ].map((item, idx) => (
              <RevealOnScroll key={item.t} delay={idx * 80}>
                <div className="bg-white rounded-xl p-6 border border-brand-cream-3 text-center">
                  <div className="w-10 h-10 flex items-center justify-center bg-brand-green/10 rounded-full mx-auto mb-3">
                    <i className={`${item.icon} text-brand-green text-lg`} />
                  </div>
                  <h3 className="font-bold text-brand-dark text-sm mb-2">{item.t}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{item.d}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="bg-white py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <RevealOnScroll className="text-center">
            <h2 className="font-display text-2xl font-bold text-brand-dark mb-3">Service Areas Near {location.city}</h2>
            <p className="text-gray-600 text-sm mb-5">
              Serving {location.city} and surrounding {location.county} communities with expert roofing and exterior construction services.
            </p>
            <Link to="/locations" className="inline-flex items-center gap-1.5 text-brand-green font-bold text-sm hover:underline cursor-pointer">
              View All Service Areas <i className="ri-arrow-right-line text-xs" />
            </Link>
          </RevealOnScroll>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-brand-dark py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <RevealOnScroll className="text-center">
            <h2 className="font-display text-2xl font-bold text-white mb-6">What {location.city} Clients Say</h2>
            <div className="bg-white/8 border border-white/10 rounded-xl p-7">
              <div className="flex items-center justify-center gap-0.5 mb-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-brand-gold text-lg" />
                ))}
              </div>
              <p className="text-white/80 italic font-display text-base leading-relaxed mb-4">
                &ldquo;Green World delivered exceptional work on our {location.city} home. Professional communication,
                quality materials, clean installation, and fair pricing. The family-owned difference is real.&rdquo;
              </p>
              <div className="text-gray-400 text-sm">— Verified Client, {location.city}, IL</div>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-brand-cream py-14">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <RevealOnScroll>
            <h2 className="font-display text-2xl font-bold text-brand-dark mb-6">{location.city} Roofing FAQ</h2>
          </RevealOnScroll>
          <div className="flex flex-col gap-4">
            {[
              {
                q: `Who provides roofing services in ${location.city}?`,
                a: `Green World Construction and Roofing offers professional roofing, tuckpointing, siding, and exterior construction services throughout ${location.city} and the broader ${location.county} area. Family-owned since 1989 with 35+ years of local expertise.`,
              },
              {
                q: `How much do roofing services cost in ${location.city}?`,
                a: `Pricing varies depending on project size, materials, and scope. We provide free on-site inspections and detailed written estimates for all ${location.city} projects. No hidden fees, no verbal quotes — every estimate is itemized and in writing.`,
              },
              {
                q: `How long do roofing projects take in ${location.city}?`,
                a: `Most residential projects in ${location.city} are completed within one to several days. Larger commercial or complex projects may require additional time. We provide firm timelines in every written estimate before work begins.`,
              },
            ].map((item, idx) => (
              <RevealOnScroll key={idx} delay={idx * 70}>
                <div className="bg-white rounded-xl p-6 border border-brand-cream-3">
                  <h3 className="font-bold text-brand-dark mb-3 text-sm flex items-start gap-2">
                    <i className="ri-question-line text-brand-green text-sm mt-0.5 flex-shrink-0" />
                    {item.q}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-green py-16">
        <RevealOnScroll className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl font-bold text-white mb-3">Upgrade Your Property in {location.city} Today</h2>
          <p className="text-white/75 mb-8">Schedule your free inspection and get a customized roofing or exterior construction plan.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact" className="bg-brand-gold hover:bg-brand-gold-dark text-white text-sm font-bold px-10 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap">
              Request Free Inspection
            </Link>
            <a href={PHONE_HREF} className="border-2 border-white/40 hover:border-white text-white text-sm font-bold px-10 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
              <i className="ri-phone-fill" />
              {PHONE}
            </a>
          </div>
        </RevealOnScroll>
      </section>
    </main>
  );
}
